import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuthContext } from './context/AuthProvider';
import RouteFallback from './components/common/RouteFallback';
import ProtectedRoute from './components/common/ProtectedRoute';
import RequirePermission from './components/common/RequirePermission';
import { HOME_ROUTE } from './utils/redirect';
import {
  FULFILLMENT_AUX_ROUTES,
  FULFILLMENT_TABS,
  LEGACY_REDIRECTS,
} from './pages/fulfillment/routes';

const DashboardLayout = lazy(() => import('./layouts/DashboardLayout'));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const Login = lazy(() => import('./pages/login/Login'));
const Settings = lazy(() => import('./pages/Settings/Settings'));

const BacklogTab = lazy(() => import('./pages/fulfillment/tabs/BacklogTab'));
const ProjectsTab = lazy(() => import('./pages/fulfillment/tabs/ProjectsTab'));
const PlantViewTab = lazy(() => import('./pages/fulfillment/tabs/PlantViewTab'));
const TrackingTab = lazy(() => import('./pages/fulfillment/tabs/TrackingTab'));
const AtpTab = lazy(() => import('./pages/fulfillment/tabs/AtpTab'));
const ExecSummaryTab = lazy(() => import('./pages/fulfillment/tabs/ExecSummaryTab'));
const UploadsPage = lazy(() => import('./pages/fulfillment/uploads/UploadsPage'));
const FulfillmentLayout = lazy(() => import('./pages/fulfillment/FulfillmentLayout'));

/** Path → element. Mirrors the fulfillment route config. */
const PAGE_ELEMENTS = {
  '/sales-order-backlog': <BacklogTab />,
  '/projects': <ProjectsTab />,
  '/plant-view': <PlantViewTab />,
  '/weekly-tracking': <TrackingTab />,
  '/atp': <AtpTab />,
  '/executive-summary': <ExecSummaryTab />,
  '/uploads': <UploadsPage />,
};

const App = () => {
  const { authState, bootstrapping } = useAuthContext();

  // Hold ALL routing until the session probe settles. Rendering routes first
  // would flash the login page at a signed-in user reloading a deep link, and
  // then replace their URL with /login — losing where they were.
  if (bootstrapping) return <RouteFallback />;

  return (
    <div>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public. Already signed in? There is nothing to log into — go home,
              so a stray /login never presents a second sign-in form. */}
          <Route
            path="/login"
            element={authState?.isLoggedIn ? <Navigate to={HOME_ROUTE} replace /> : <Login />}
          />

          <Route element={<ProtectedRoute />}>
            <Route element={<DashboardLayout />}>
              {/* Operational fulfillment pages share a lightweight page frame;
                  each remains independently routable for direct navigation. */}
              <Route element={<FulfillmentLayout />}>
                {FULFILLMENT_TABS.map((p) => (
                  <Route key={p.path} path={p.path} element={PAGE_ELEMENTS[p.path]} />
                ))}
              </Route>
              {/* Uploads replaces the dataset EVERY user sees, so it needs the
                  upload write grant, not merely fulfillment read. Without this
                  gate a VIEWER could type /uploads and get the page — the
                  controls degrade, but the page should not be reachable. */}
              <Route
                element={<RequirePermission feature="FULFILLMENT_UPLOAD" permission="write" />}
              >
                {FULFILLMENT_AUX_ROUTES.map((p) => (
                  <Route key={p.path} path={p.path} element={PAGE_ELEMENTS[p.path]} />
                ))}
              </Route>

              {/* Bookmarks from when these were tabs. */}
              {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
                <Route key={from} path={from} element={<Navigate to={to} replace />} />
              ))}

              <Route path="/dashboard" element={<Dashboard />} />
              {/* ADMIN-only: rbac.constants.js gives USER_MGMT to ADMIN alone.
                  Settings already rendered no user data without it — the API
                  refuses — but the shell rendered for anyone who typed the URL. */}
              <Route element={<RequirePermission feature="USER_MGMT" permission="write" />}>
                <Route path="/settings" element={<Settings />} />
              </Route>
              <Route path="/" element={<Navigate to={HOME_ROUTE} replace />} />
              {/* Catch-all sits INSIDE the guard so an unknown path from a
                  signed-out visitor lands on /login, not on the dashboard. */}
              <Route path="*" element={<Navigate to={HOME_ROUTE} replace />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </div>
  );
};

export default App;
